import type { GeneralResMock, Meta } from './general.type'

export type ArchiveFolderType = 'archive_folder'
export type ArchiveDocumentType = 'archive_document'

export type ArchiveDigitizationStatus = 'partially_scanned' | 'fully_scanned' | 'physical_only'

export interface ArchiveResourceLinks {
  self: string
  related: string
}

export type ArchiveFolderCreateAttributes = Omit<ArchiveFolderAttributes, 'created_at' | 'updated_at'> & {
  patient_id: number
  facility_id: number
}

export type ArchiveFolderCreatePayload = {
  data: {
    type: ArchiveFolderType
    attributes: ArchiveFolderCreateAttributes
  }
}

export interface ArchiveFolderAttributes {
  folder_number: string
  date_range_start: string
  date_range_end: string
  digitization_status: ArchiveDigitizationStatus
  physical_location_name: string
  shelf: string
  column: string
  section: string
  created_at: string
  updated_at: string
}

export interface ArchiveFolderRelationships {
  patient?: {
    data: { id: string; type: 'patient' } | null
  }
  facility?: {
    data: { id: string; type: 'facility' } | null
  }
  created_by?: {
    data: { id: string; type: 'staff' } | null
  }
  archive_documents?: {
    data: { id: string; type: ArchiveDocumentType }[]
  }
}

export interface ArchiveFolderResource {
  id: string
  type: ArchiveFolderType
  attributes: ArchiveFolderAttributes
  links: ArchiveResourceLinks
  meta: Record<string, unknown>
  relationships: ArchiveFolderRelationships
}

export interface ArchiveFolderPatientIncluded {
  id: string
  type: 'patient'
  attributes: {
    first_name?: string
    last_name?: string
  }
  links?: ArchiveResourceLinks
  meta?: Record<string, unknown>
  relationships?: Record<string, never>
}

export interface ArchiveFolderFacilityIncluded {
  id: string
  type: 'facility'
  attributes: {
    name?: string
  }
  links?: ArchiveResourceLinks
  meta?: Record<string, unknown>
  relationships?: Record<string, never>
}

export interface ArchiveFolderStaffIncluded {
  id: string
  type: 'staff'
  attributes: {
    first_name?: string
    last_name?: string
  }
  links?: ArchiveResourceLinks
  meta?: Record<string, unknown>
  relationships?: Record<string, never>
}

export interface ArchiveFolderDocumentIncluded {
  id: string
  type: ArchiveDocumentType
  attributes: Record<string, unknown>
  links?: ArchiveResourceLinks
  meta?: Record<string, unknown>
  relationships?: Record<string, never>
}

export type ArchiveFolderIncluded =
  | ArchiveFolderPatientIncluded
  | ArchiveFolderFacilityIncluded
  | ArchiveFolderStaffIncluded
  | ArchiveFolderDocumentIncluded

export interface ArchiveStatsMeta extends Meta {
  stats?: {
    total_files?: number
    checked_out?: number
    overdue_checkouts?: number
    scanned_documents?: number
    scanned_this_month?: number
    total_documents?: number
    files_with_scans?: number
    uploaded_this_month?: number
    awaiting_review?: number
    digitization_rate?: number
    fully_digitized?: number
  }
}

export type ArchiveFoldersResponse = GeneralResMock<ArchiveFolderResource[], ArchiveFolderIncluded[], ArchiveStatsMeta>

export interface ArchiveDocumentFile {
  id: number
  type: 'attachment'
  filename: string
  content_type: string
  byte_size: number
  url: string
}

export interface ArchiveDocumentAttributes {
  file_number: string
  title: string
  document_type: string
  specialty: string
  document_date: string
  clinical_context: string
  diagnosis: string
  digitized_at: string
  created_at: string
  updated_at: string
  patient_name: string
  files: ArchiveDocumentFile[]
}

export interface ArchiveDocumentRelationships {
  archive_folder?: {
    data: { id: string; type: ArchiveFolderType } | null
  }
  created_by?: {
    data: { id: string; type: 'staff' } | null
  }
  doctor?: {
    data: { id: string; type: 'staff' } | null
  }
}

export interface ArchiveDocumentResource {
  id: string
  type: ArchiveDocumentType
  attributes: ArchiveDocumentAttributes
  links?: ArchiveResourceLinks
  meta?: Record<string, unknown>
  relationships: ArchiveDocumentRelationships
}

/** Partial folder shape returned in archive_document included */
export interface ArchiveDocumentFolderIncluded {
  id: string
  type: ArchiveFolderType
  attributes: {
    folder_number?: string
    digitization_status?: ArchiveDigitizationStatus
  }
  links?: ArchiveResourceLinks
  meta?: Record<string, unknown>
}

export interface ArchiveDocumentStaffIncluded {
  id: string
  type: 'staff'
  attributes: {
    first_name?: string
    middle_name?: string | null
    last_name?: string
  }
  links?: ArchiveResourceLinks
  meta?: {
    status?: string
    roles?: string[]
    [key: string]: unknown
  }
}

export type ArchiveDocumentIncluded = ArchiveDocumentFolderIncluded | ArchiveDocumentStaffIncluded

export type ArchiveDocumentsResponse = GeneralResMock<ArchiveDocumentResource[], ArchiveDocumentIncluded[]>

export type ArchiveDigitizationStatusStat = {
  count: number
  percentage: number
}

export type ArchiveAuditReportStats = {
  total_files: number
  scanned_documents: number
  scanned_this_month: number
  digitization_rate: number
  fully_digitized: number
}

export type ArchiveDigitizationStatusBreakdown = {
  physical_only: ArchiveDigitizationStatusStat
  partially_digitized: ArchiveDigitizationStatusStat
  fully_digitized: ArchiveDigitizationStatusStat
}

export type ArchiveScansPerMonth = {
  month: string
  label: string
  count: number
}

export type ArchiveAuditLogEntry = {
  id?: string | number
  action?: string
  auditable_type?: string
  auditable_id?: string | number
  staff?: string
  user_id?: string | number
  created_at?: string
  details?: string
  [key: string]: unknown
}

export type ArchiveAuditReportData = {
  stats: ArchiveAuditReportStats
  digitization_status: ArchiveDigitizationStatusBreakdown
  scans_per_month: ArchiveScansPerMonth[]
  audit_logs: ArchiveAuditLogEntry[]
}

export type ArchiveAuditReportResponse = {
  data: ArchiveAuditReportData
}

export type ArchiveDocumentCreateAttributes = {
  archive_folder_id: number
  file_number: string
  title: string
  document_type: string
  specialty?: string
  document_date: string
  clinical_context?: string
  diagnosis?: string
  file_signed_ids: string[]
}

export type ArchiveDocumentCreatePayload = {
  data: {
    type: ArchiveDocumentType
    attributes: ArchiveDocumentCreateAttributes
  }
}

export type ScannedDocumentCategory = 'all' | 'consultations' | 'discharge' | 'prescriptions'

export interface ScannedDocumentItem {
  id: string
  title: string
  patientName: string
  department: string
  date: string
  pages: number
  type: string
  typeKey: 'lab_result' | 'prescription' | 'discharge' | 'consultation' | 'imaging' | 'histology'
  category: Exclude<ScannedDocumentCategory, 'all'>
}

export function formatArchiveFolderLocation(attrs: ArchiveFolderAttributes): string {
  const { physical_location_name, section, shelf, column } = attrs
  if (physical_location_name && section && shelf) {
    return `${physical_location_name}. ${section}-${shelf}`
  }
  return [physical_location_name, column, section, shelf].filter(Boolean).join('. ')
}

export function getArchivePatientName(folder: ArchiveFolderResource, included?: ArchiveFolderIncluded[]): string {
  const patientId = folder.relationships.patient?.data?.id
  if (!patientId || !included?.length) return '—'
  const patient = included.find(item => item.type === 'patient' && item.id === patientId)
  if (!patient || patient.type !== 'patient') return '—'
  const { first_name, last_name } = patient.attributes
  return [first_name, last_name].filter(Boolean).join(' ') || '—'
}

export function getArchiveFacilityName(folder: ArchiveFolderResource, included?: ArchiveFolderIncluded[]): string {
  const facilityId = folder.relationships.facility?.data?.id
  if (!facilityId || !included?.length) return '—'
  const facility = included.find(item => item.type === 'facility' && item.id === facilityId)
  if (!facility || facility.type !== 'facility') return '—'
  return facility.attributes.name || '—'
}

export function getArchiveDocumentFolderNumber(
  doc: ArchiveDocumentResource,
  included?: ArchiveDocumentIncluded[]
): string {
  const folderId = doc.relationships.archive_folder?.data?.id
  if (folderId && included?.length) {
    const folder = included.find(item => item.type === 'archive_folder' && item.id === folderId)
    if (folder && folder.type === 'archive_folder' && folder.attributes.folder_number) {
      return folder.attributes.folder_number
    }
  }
  return doc.attributes.file_number || '—'
}

export function getArchiveDocumentCreatedByName(
  doc: ArchiveDocumentResource,
  included?: ArchiveDocumentIncluded[]
): string {
  const staffId = doc.relationships.created_by?.data?.id
  if (!staffId || !included?.length) return '—'
  const staff = included.find(item => item.type === 'staff' && item.id === staffId)
  if (!staff || staff.type !== 'staff') return '—'
  const { first_name, middle_name, last_name } = staff.attributes
  return [first_name, middle_name, last_name].filter(Boolean).join(' ') || '—'
}
